import React, { FC, useMemo } from 'react';
import { NavLink, Route, Switch, useRouteMatch } from 'react-router-dom';
import { Badge, Col, Nav, Row } from 'reactstrap';
import { ExtendedUser } from '../../../../shared/user/extended';
import { ElementHeader, UserStateComponent } from '../../../components';
import {
  checkPermission,
  classNames,
  Fetcher,
  useConvention,
  useFetcher,
  useUserSafe,
} from '../../../utils';
import { UserAuditTab } from './auditlogs';
import { UserDealerTab } from './dealertab';
import { UserPermissionsTab } from './permissionstab';
import { RolesTab } from './rolestab';
import { UserEmergencyTab } from './useremergencytab';
import { UserBadges } from './utils';
import { UserVolunteerTab } from './volunteertab';
import { NotesTab, UserOrdersTab, UserProfileTab } from '.';

interface TabDetails {
  name: string;
  link: string;
  readonly getCount?: () => number;
  readonly getBody: () => React.JSX.Element;
  readonly enabled?: () => boolean;
}

function getDisplayName(userData: ExtendedUser): string {
  const { firstName, lastName, id, preferredName, username } = userData;

  if (firstName && lastName) {
    if (preferredName) {
      return `${preferredName} (${firstName} ${lastName}) [ID: ${id}]`;
    }

    return `${firstName} ${lastName} [ID: ${id}]`;
  }

  if (username) {
    return `${username} [ID: ${id}]`;
  }

  return `[ID: ${id}]`;
}

export const UserProfile: FC = () => {
  const user = useUserSafe();
  const match = useRouteMatch<{ id: string }>();
  const userId = Number.parseInt(match.params.id, 10);
  const convention = useConvention();
  const fetch = useFetcher(async () => {
    return await api.getExtendedUser(userId);
  }, [userId]);

  const { data, refresh } = fetch;
  const tabs = useMemo<TabDetails[]>(() => {
    if (!data) {
      return [];
    }

    return [
      {
        getBody: () => <UserProfileTab refresh={refresh} userData={data} />,
        link: '',
        name: 'User',
      },
      {
        getBody: () => <NotesTab userData={data} />,
        getCount: () => data.userNoteCount,
        link: '/notes',
        name: 'Notes',
      },
      {
        enabled: () => checkPermission(user, 'order:view') && !data.owner,
        getBody: () => <UserOrdersTab refreshContents={refresh} userId={userId} />,
        getCount: () => data.orderCount ?? 0,
        link: '/orders',
        name: 'Orders',
      },
      {
        enabled: () => !!data.roles && checkPermission(user, 'user:roles:update'),
        getBody: () => <RolesTab refreshContents={refresh} userData={data} />,
        getCount: () =>
          data.roles?.filter(
            (t) => t.conventionIds.includes(0) || t.conventionIds.includes(convention.id),
          ).length ?? 0,
        link: '/roles',
        name: 'Roles',
      },
      {
        enabled: () => checkPermission(user, 'system:permission:read'),
        getBody: () => (
          <UserPermissionsTab
            canEdit={checkPermission(user, 'system:permission:update')}
            userId={userId}
          />
        ),
        link: '/permissions',
        name: 'Permissions',
      },
      {
        enabled: () => checkPermission(user, 'volunteer:read') && !data.owner,
        getBody: () => <UserVolunteerTab user={data} />,
        link: '/volunteer',
        name: 'Volunteer',
      },
      {
        enabled: () => checkPermission(user, 'vendor:read') && !data.owner,
        getBody: () => <UserDealerTab user={data} />,
        link: '/dealer',
        name: 'Dealer',
      },
      {
        enabled: () => !!data.registration && checkPermission(user, 'registration:read:pii'),
        getBody: () => <UserEmergencyTab userData={data} />,
        link: '/contacts',
        name: 'Emergency Contact(s)',
      },
      {
        enabled: () => checkPermission(user, 'audit:read'),
        getBody: () => <UserAuditTab userId={data.id} />,
        link: '/auditlogs',
        name: 'Audit',
      },
    ];
  }, [data, refresh, user, userId, convention.id]);

  if (!data || data.id !== userId) {
    return <Fetcher result={fetch} />;
  }

  return (
    <UserStateComponent>
      <ElementHeader icon={{ url: data.profilePictureUrl }} title={getDisplayName(data)}>
        <UserBadges user={data} />
      </ElementHeader>
      <Row>
        <Col
          className={classNames(
            {
              'profile-loading': fetch.loading,
            },
            'mb-10',
          )}
          xs={12}
        >
          <Nav tabs>
            {tabs
              .filter((tab) => !tab.enabled || tab.enabled())
              .map((tab) => (
                <NavLink
                  activeClassName="active"
                  className="nav-link"
                  exact={tab.link === ''}
                  id={`${tab.name}_tab`}
                  key={tab.name}
                  to={`${match.url}${tab.link}`}
                >
                  {tab.name}
                  {tab.getCount && (
                    <>
                      &nbsp;
                      <Badge color={tab.getCount() === 0 ? 'secondary' : 'primary'}>
                        {tab.getCount()}
                      </Badge>
                    </>
                  )}
                </NavLink>
              ))}
          </Nav>
        </Col>
      </Row>
      <UserRoutes tabs={tabs} />
    </UserStateComponent>
  );
};

const UserRoutes: FC<{ readonly tabs: TabDetails[] }> = ({ tabs }) => {
  const { path } = useRouteMatch();

  return (
    <Switch>
      {tabs
        .filter((tab) => !tab.enabled || tab.enabled())
        .map((tab) => (
          <Route component={tab.getBody} exact key={tab.name} path={`${path}${tab.link}`} />
        ))}
    </Switch>
  );
};
